/**
 * ArtStatsSparkline — mini-gráfico 30 dias de views × usos de uma arte
 * (PSHWO-64518).
 *
 * SVG inline de propósito: o admin não tem lib de gráfico e uma sparkline
 * de duas séries não justifica uma dependência nova (regra 0 do padrão TMF —
 * 1 lib canônica por função; nenhuma catalogada aqui ainda).
 *
 * Cores saem de tokens do design system, então dark e light seguem juntos:
 *   - views → `--color-primary`
 *   - usos  → `--color-success`
 *
 * Acessibilidade: o SVG é `role="img"` com `aria-label` descrevendo os
 * totais; o resumo textual abaixo repete os números para quem não enxerga
 * a curva.
 */

import type { ArtDailyPoint } from "@/hooks/useArtStats";

const WIDTH = 120;
const HEIGHT = 28;
const PAD = 2;

export interface ArtStatsSparklineProps {
  /** Série diária (mais antigo → mais recente). */
  points: ArtDailyPoint[];
  /** Rótulo da arte, usado na descrição acessível. */
  label?: string;
}

/**
 * Converte a série em `points` de <polyline>, normalizando pelo pico das
 * DUAS séries (escala compartilhada — senão as curvas mentem na comparação).
 */
function toPolyline(values: number[], max: number): string {
  if (values.length === 0) return "";
  const usableW = WIDTH - PAD * 2;
  const usableH = HEIGHT - PAD * 2;
  const stepX = values.length > 1 ? usableW / (values.length - 1) : 0;
  return values
    .map((v, i) => {
      const x = PAD + i * stepX;
      const y = PAD + usableH - (max > 0 ? (v / max) * usableH : 0);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

export function ArtStatsSparkline({ points, label }: ArtStatsSparklineProps) {
  const views = points.map((p) => p.views);
  const uses = points.map((p) => p.uses);
  const totalViews = views.reduce((a, b) => a + b, 0);
  const totalUses = uses.reduce((a, b) => a + b, 0);
  const max = Math.max(0, ...views, ...uses);

  const description =
    `Últimos 30 dias${label ? ` de ${label}` : ""}: ` +
    `${totalViews} visualizações e ${totalUses} usos.`;

  return (
    <div className="space-y-1">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-7 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={description}
      >
        <line
          x1={PAD}
          y1={HEIGHT - PAD}
          x2={WIDTH - PAD}
          y2={HEIGHT - PAD}
          stroke="var(--color-border)"
          strokeWidth="0.5"
        />
        {max > 0 ? (
          <>
            <polyline
              points={toPolyline(views, max)}
              fill="none"
              stroke="var(--color-primary)"
              strokeWidth="1.25"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <polyline
              points={toPolyline(uses, max)}
              fill="none"
              stroke="var(--color-success)"
              strokeWidth="1.25"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        ) : null}
      </svg>
      <p className="flex items-center gap-2 font-mono text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
        <span className="flex items-center gap-1">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-primary)]"
            aria-hidden="true"
          />
          {totalViews} vistas
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-success)]"
            aria-hidden="true"
          />
          {totalUses} usos
        </span>
        <span className="ml-auto uppercase tracking-[0.08em]">30d</span>
      </p>
    </div>
  );
}
